<template>
  <div class="container mx-auto p-4">
    <!-- Header -->
    <div class="mb-6 text-center">
      <h1 class="text-3xl font-bold text-gray-900">My Assigned Jobs</h1>
      <p class="text-gray-600">Showing all jobs linked to your access token.</p>
    </div>

    <!-- Jobs List -->
    <div v-if="isLoading" class="text-center text-gray-500">Loading jobs...</div>
    <div v-else-if="jobs.length" class="space-y-4">
      <div
        v-for="(b, idx) in jobs"
        :key="b.id || idx"
        class="border rounded-lg shadow-sm bg-white overflow-hidden"
      >
        <!-- Header row (click to expand) -->
        <button
          @click="toggleExpand(idx)"
          class="w-full text-left flex justify-between items-center px-6 py-4 bg-gray-50 border-b border-gray-200 focus:outline-none"
        >
          <div>
            <h2 class="text-lg font-semibold text-gray-900">
              {{ b.id }} — {{ b.vesselName || 'No Vessel' }} ({{ b.counterNumber || 'No Counter' }})
            </h2>
            <p class="text-sm text-gray-600">
              {{ getDisplayStatus(b.status) }} • {{ formatDT(b.startTime) }}
            </p>
          </div>
          <span class="flex items-center gap-2">
            <span class="badge" :class="getStatusColor(b.status)">
              {{ getDisplayStatus(b.status) }}
            </span>
            <i
              class="bi"
              :class="expanded[idx] ? 'bi-chevron-up' : 'bi-chevron-down'"
            ></i>
          </span>
        </button>

        <!-- Expandable details -->
        <transition name="fade">
          <div v-if="expanded[idx]" class="px-6 py-4 bg-white">
            <p class="text-sm text-gray-600 mb-2">
              📍 <strong>Start:</strong> {{ b.rawStartLocation || '—' }}
            </p>
            <p class="text-sm text-gray-600 mb-4">
              🏁 <strong>End:</strong> {{ b.rawEndLocation || '—' }}
            </p>
            <a
              :href="`https://amdtransportation.com.sg/jobStartEnd/${b.id}/${token}`"
              class="inline-block bg-green-600 hover:bg-green-700 text-white text-sm font-semibold py-2 px-4 rounded-lg"
            >
              View Job Details
            </a>
          </div>
        </transition>
      </div>
    </div>

    <div v-else class="text-center text-gray-500">No jobs found for this token.</div>
  </div>
</template>

<script>
import axios from "axios";
import { useToast } from "vue-toastification";

export default {
  name: "JobList",
  data() {
    return {
      token: this.$route.params.token || "",
      jobs: [],
      expanded: {},
      isLoading: true,
      frontendBase: "amdtransportation.com.sg"
    };
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  methods: {
    toggleExpand(idx) {
      this.expanded[idx] = !this.expanded[idx];
      // make Vue detect reactivity change properly
      this.expanded = { ...this.expanded };
    },
    async fetchJobsByToken() {
      try {
        const res = await axios.get(`${this.$apiURL}/api/bookings/by-token/full/${this.token}`);
        const data = res.data || {};
        this.jobs = data.bookings || [];
      } catch (err) {
        console.error("❌ fetchJobsByToken error:", err);
        this.toast.error("Invalid or expired token.");
      } finally {
        this.isLoading = false;
      }
    },
    getDisplayStatus(status) {
      if (!status) return "—";
      if (status.toLowerCase() === "failure") return "Cancelled";
      if (status.toLowerCase() === "dop") return "Completed";
      return status;
    },
    getStatusColor(status) {
      const lower = (status || "").toLowerCase();
      if (lower === "new") return "bg-blue-100 text-blue-800";
      if (lower === "assigned") return "bg-indigo-100 text-indigo-800";
      if (lower === "ongoing" || lower === "on route") return "bg-yellow-100 text-yellow-800";
      if (lower === "completed" || lower === "dop") return "bg-green-100 text-green-800";
      if (lower === "cancelled" || lower === "failure") return "bg-red-100 text-red-800";
      return "bg-gray-100 text-gray-800";
    },
    formatDT(iso) {
      if (!iso) return "—";
      try {
        const d = new Date(iso);
        return d.toLocaleString("en-SG", { hour12: false });
      } catch (e) {
        return iso;
      }
    },
    async verifyAccessToken(token) {
      try {
        const res = await axios.get(`${this.$apiURL}/api/token/verify`, {
          params: { token }
        })
        if (res.data.ok) {
          this.role = res.data.role || 'driver'
          this.toast.success('Access verified')
          return true
        }
      } catch (err) {
        console.error('❌ verifyAccessToken error:', err)
      }
      return false
    },
  },
  async mounted() {
    this.token = this.$route.params.token
    if (!this.token) {
      this.toast.error('Missing access token')
      return
    }

    // ✅ Verify token first
    const ok = await this.verifyAccessToken(this.token)
    if (!ok) {
      this.toast.error('Invalid or expired token')
      return
    }
    await this.fetchJobsByToken();
  },
};
</script>

<style scoped>
.badge {
  display: inline-block;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
}
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
